import { TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

export default function PhoneChips(props) {
    const [phoneValue, setPhoneValue] = useState(null);
    const [error, setError] = useState(null);
    // Polite live-region text announced by screen readers on add/remove (AC-4).
    const [announcement, setAnnouncement] = useState("");

    const inputRef = useRef(null);
    const errorRef = useRef(null);
    // Per-chip remove-button refs + a pending-focus target applied after the list re-renders, so focus
    // lands on the next remaining chip's remove button (or the input when the list empties) (AC-5).
    const removeButtonRefs = useRef([]);
    const pendingFocusRef = useRef(null);

    const PHONE_REGEX = /^\d{10}$/;

    const PHONE_MAX_DIGITS = 10;

    const stripNonDigits = value => (value || '').replace(/\D/g, '');

    const phoneAlreadyInList = phone => props.phonesList.includes(phone);

    const focusInput = () => {
        if (inputRef.current) inputRef.current.focus();
    };

    // When an error appears, scroll it into view so the message is visible (AC-2). NVDA picks it up
    // via role="alert" on the message element; scrolling guarantees a sighted user also sees it.
    useEffect(() => {
        if (error && errorRef.current && typeof errorRef.current.scrollIntoView === "function") {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [error]);

    // After a removal re-renders the list, move focus to the resolved target (next remaining chip's
    // remove button, or the input when the list is now empty) (AC-5).
    useEffect(() => {
        if (pendingFocusRef.current == null) return;
        const target = pendingFocusRef.current;
        pendingFocusRef.current = null;
        if (target === "input") {
            focusInput();
        } else {
            const btn = removeButtonRefs.current[target];
            if (btn) btn.focus();
            else focusInput();
        }
    }, [props.phonesList]);

    const isValid = value => {
        let e = null;

        if (phoneAlreadyInList(value)) e = `${value} has already been added`;

        if (!PHONE_REGEX.test(value)) e = "Phone number must be exactly 10 digits";

        setError(e);

        if (e) return false;

        return true;
    }

    const handleKeyDown = event => {
        if (["Enter", "Tab", ","].includes(event.key)) {
            var value = phoneValue ? stripNonDigits(phoneValue) : '';

            if (!value) {
                if (event.key === 'Tab' && props.onTabOut) {
                    event.preventDefault();
                    props.onTabOut();
                }
                return;
            }

            event.preventDefault();

            if (isValid(value)) {
                if (props.onPhoneEmpty) props.onPhoneEmpty();
                setPhoneValue(null);
                props.addPhones(value);
                setAnnouncement(`Target phone number ${value} added`);
            }
        }
    }

    const handleChange = event => {
        // Only digits are allowed and never more than 10 — block typing an 11th digit in the UI.
        const digits = stripNonDigits(event.target.value).slice(0, PHONE_MAX_DIGITS);

        if (digits.length > 0 && props.onPhoneInput) props.onPhoneInput();
        else if (props.onPhoneEmpty && digits.length === 0) props.onPhoneEmpty();

        if (error) setError(null);

        setPhoneValue(digits);
    };

    const handlePaste = event => {
        event.preventDefault();

        var paste = event.clipboardData.getData("text");
        var digits = stripNonDigits(paste);

        if (digits) {
            var chunks = digits.match(/\d{10}/g);

            if (chunks) {
                var toBeAdded = chunks.filter(phone => !phoneAlreadyInList(phone));

                if (toBeAdded.length > 0) {
                    props.addPhones(...toBeAdded);
                    setAnnouncement(
                        toBeAdded.length === 1
                            ? `Target phone number ${toBeAdded[0]} added`
                            : `${toBeAdded.length} target phone numbers added`
                    );
                }
            }
        }
    }

    const handleRemove = (index, value) => {
        const remaining = props.phonesList.length - 1;
        // Move focus to the next remaining chip's remove button. After removal the list shifts left,
        // so the chip that was at index+1 becomes index. If we removed the last chip, focus the new
        // last one (index-1). When no chips remain, fall back to the input (AC-5).
        if (remaining <= 0) {
            pendingFocusRef.current = "input";
        } else if (index < remaining) {
            pendingFocusRef.current = index;
        } else {
            pendingFocusRef.current = remaining - 1;
        }
        props.removeItem(index);
        setAnnouncement(`Target phone number ${value} removed`);
    };

    return (<div className="emailChipsContainer">
        <section className="emailChipsDisplayList">
            {props.phonesList.map((v, i) => (<div aria-label="phone numbers" className="emailChip" key={`phoneChip${i}`}>
                {v}
                {/* Unique, descriptive name so each button announces its own number (AC-1). */}
                <button aria-label={`Remove target phone number ${v}`} className="emailChipRemoveButton"
                        ref={el => { removeButtonRefs.current[i] = el; }}
                        onClick={() => handleRemove(i, v)}>
                    &times;
                </button>
            </div>))}
            <TextField className={`emailChipsInput ${error && 'hasError'}`} value={phoneValue || ''}
                       placeholder={props.inputPlaceholder || "Type or paste phone numbers and press `Enter`..."}
                       onKeyDown={handleKeyDown}
                       onChange={handleChange}
                       onPaste={handlePaste}
                       fullWidth
                       autoComplete="off"
                       inputRef={inputRef}
                       inputProps={{
                           "aria-label": props.inputAriaLabel || "phone numbers",
                           ...(props.inputAriaRequired && { "aria-required": props.inputAriaRequired }),
                           // Mark invalid for assistive tech, but do NOT add aria-describedby pointing at
                           // the alert — role="alert" already announces it once; describedby would make
                           // NVDA read the same message a second time when focus is in the field (AC-2).
                           ...(error && { "aria-invalid": true }),
                           "data-1p-ignore": true,
                           "data-lpignore": true
                       }}
            />
            {/* Always-mounted assertive live region: announces ONLY the message text on change (no
                "alert" role word), exactly once. Visible + occupies space only when there's an error (AC-2). */}
            <p ref={errorRef} className={error ? "error" : "visuallyHidden"} aria-live="assertive">{error || ''}</p>
        </section>
        {/* Visually-hidden polite live region for add/remove announcements (AC-4). */}
        <div aria-live="polite" role="status" className="visuallyHidden">{announcement}</div>
    </div>);
}
